// The Halloween lesson's look: a night backdrop with a moon, a haunted house and dead trees, a ghost
// that drifts across it, a candle burning in the lower-left corner, and the spider that crawls in
// where every other lesson gets the ladybird. Used only while the Halloween lesson is active — see
// isHalloweenPack (src/game/starGlyphs.ts) and the callers in BackdropScene and BoardScene.
//
// Everything is drawn in code, like the rest of the game's furniture: no image to download, nothing
// added to the precache, nothing to localise. Each picture is painted ONCE onto a canvas texture
// (the texture manager is shared by every scene and keeps them for the life of the page, so the
// second round pays nothing) and then moved around as plain sprites. A Phaser Graphics object would
// be re-tessellated on every frame (see pillTexture in roundedLabelBg.ts); a sprite is a quad.
//
// The centre of the screen is kept dark and quiet on purpose: the words the child has to read sit
// there, in white, and a busy picture behind them is a worse lesson.
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/game/config';

type Ctx = CanvasRenderingContext2D;

/** Paint a texture once. A no-op when the key already exists (every round, every scene). */
function paintOnce(
  scene: Phaser.Scene, key: string, w: number, h: number, draw: (ctx: Ctx) => void,
): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return;
  draw(tex.getContext());
  tex.refresh();
}

/** Deterministic RNG so the scenery is the same picture every round. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

// ── backdrop ──────────────────────────────────────────────────────────────────────────────────

const KEY_BG = 'hw_backdrop';

/** A bare tree: a trunk that forks into thinner, shorter branches. */
function drawTree(
  ctx: Ctx, x: number, y: number, len: number, angle: number, width: number, depth: number,
  rnd: () => number,
): void {
  if (depth === 0 || len < 6) return;
  const x2 = x + Math.cos(angle) * len;
  const y2 = y + Math.sin(angle) * len;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  const forks = depth > 3 ? 2 : 2 + (rnd() < 0.4 ? 1 : 0);
  for (let i = 0; i < forks; i++) {
    const spread = (i - (forks - 1) / 2) * (0.5 + rnd() * 0.3) + (rnd() - 0.5) * 0.25;
    drawTree(ctx, x2, y2, len * (0.68 + rnd() * 0.1), angle + spread, width * 0.66, depth - 1, rnd);
  }
}

function drawHouse(ctx: Ctx, x: number, y: number, s: number): void {
  ctx.fillStyle = '#0c0614';
  ctx.beginPath();
  // body + roof + tower, as one silhouette standing on (x, y)
  ctx.moveTo(x - 70 * s, y);
  ctx.lineTo(x - 70 * s, y - 70 * s);
  ctx.lineTo(x - 40 * s, y - 112 * s);          // roof, left slope
  ctx.lineTo(x - 18 * s, y - 70 * s);
  ctx.lineTo(x - 18 * s, y - 150 * s);          // tower
  ctx.lineTo(x + 2 * s, y - 182 * s);
  ctx.lineTo(x + 22 * s, y - 150 * s);
  ctx.lineTo(x + 22 * s, y - 80 * s);
  ctx.lineTo(x + 50 * s, y - 104 * s);          // roof, right slope
  ctx.lineTo(x + 76 * s, y - 72 * s);
  ctx.lineTo(x + 76 * s, y);
  ctx.closePath();
  ctx.fill();
  // lit windows
  ctx.shadowColor = '#ffa030';
  ctx.shadowBlur = 14 * s;
  ctx.fillStyle = '#ffb347';
  for (const [wx, wy, ww, wh] of [[-50, -52, 14, 20], [-2, -128, 10, 16], [30, -50, 14, 20], [4, -52, 12, 18]]) {
    ctx.fillRect(x + wx * s, y + wy * s, ww * s, wh * s);
  }
  ctx.shadowBlur = 0;
}

function drawPumpkin(ctx: Ctx, x: number, y: number, r: number): void {
  ctx.shadowColor = '#ff8a1f';
  ctx.shadowBlur = r * 0.9;
  ctx.fillStyle = '#e8670f';
  for (const dx of [-0.5, 0.5, 0, -0.25, 0.25]) {
    ctx.beginPath();
    ctx.ellipse(x + dx * r, y, r * 0.55, r * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#2f5a1c';
  ctx.fillRect(x - r * 0.08, y - r * 1.05, r * 0.16, r * 0.3);
  ctx.fillStyle = '#ffd36b';
  for (const sx of [-1, 1]) {   // eyes
    ctx.beginPath();
    ctx.moveTo(x + sx * r * 0.52, y - r * 0.25);
    ctx.lineTo(x + sx * r * 0.18, y - r * 0.25);
    ctx.lineTo(x + sx * r * 0.35, y - r * 0.55);
    ctx.fill();
  }
  ctx.beginPath();              // grin
  ctx.moveTo(x - r * 0.55, y + r * 0.15);
  ctx.quadraticCurveTo(x, y + r * 0.7, x + r * 0.55, y + r * 0.15);
  ctx.quadraticCurveTo(x, y + r * 0.35, x - r * 0.55, y + r * 0.15);
  ctx.fill();
}

function paintBackdrop(ctx: Ctx): void {
  const W = GAME_WIDTH;
  const H = GAME_HEIGHT;
  const rnd = rng(1031);

  // sky
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#080318');
  sky.addColorStop(0.55, '#1f0c3a');
  sky.addColorStop(0.85, '#4a1b4a');
  sky.addColorStop(1, '#6a2a3a');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  // stars, thinning out toward the horizon
  for (let i = 0; i < 140; i++) {
    const y = rnd() * H * 0.6;
    ctx.fillStyle = `rgba(255,255,255,${(0.2 + rnd() * 0.6) * (1 - y / (H * 0.7))})`;
    ctx.fillRect(rnd() * W, y, 1 + rnd() * 1.5, 1 + rnd() * 1.5);
  }

  // moon: glow, disc, a few craters, a cloud streak across it
  const mx = 1030;
  const my = 150;
  const glow = ctx.createRadialGradient(mx, my, 20, mx, my, 300);
  glow.addColorStop(0, 'rgba(255,190,100,0.38)');
  glow.addColorStop(1, 'rgba(255,190,100,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(mx - 300, my - 300, 600, 600);
  const disc = ctx.createRadialGradient(mx - 16, my - 16, 8, mx, my, 72);
  disc.addColorStop(0, '#fff1c8');
  disc.addColorStop(1, '#ffc46b');
  ctx.fillStyle = disc;
  ctx.beginPath();
  ctx.arc(mx, my, 72, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(214,150,70,0.35)';
  for (const [cx, cy, cr] of [[-24, -18, 13], [20, 14, 18], [-6, 32, 8], [30, -30, 7]]) {
    ctx.beginPath();
    ctx.arc(mx + cx, my + cy, cr, 0, Math.PI * 2);
    ctx.fill();
  }
  // (`filter` is ignored by browsers that lack it, which leaves the cloud harder-edged, not broken)
  ctx.filter = 'blur(7px)';
  ctx.fillStyle = 'rgba(30,12,52,0.6)';
  ctx.beginPath();
  ctx.ellipse(mx + 6, my + 24, 130, 12, -0.08, 0, Math.PI * 2);
  ctx.ellipse(mx - 40, my + 44, 100, 9, 0.05, 0, Math.PI * 2);
  ctx.fill();
  ctx.filter = 'none';

  // hills, far to near
  const hill = (color: string, base: number, amp: number, phase: number): void => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (let x = 0; x <= W; x += 20) {
      ctx.lineTo(x, base + Math.sin(x / 190 + phase) * amp + Math.sin(x / 71 + phase * 2) * amp * 0.25);
    }
    ctx.lineTo(W, H);
    ctx.closePath();
    ctx.fill();
  };
  hill('#2a1244', 560, 26, 0.6);
  drawHouse(ctx, 1110, 568, 1.0);
  hill('#160a28', 618, 22, 2.1);
  hill('#0b0414', 668, 16, 4.0);

  // dead trees framing the left and right edges (the centre stays clear)
  ctx.strokeStyle = '#0b0414';
  ctx.lineCap = 'round';
  drawTree(ctx, 70, H + 10, 150, -Math.PI / 2 - 0.12, 20, 7, rnd);
  drawTree(ctx, 1225, H + 10, 125, -Math.PI / 2 + 0.1, 16, 7, rnd);

  // tombstones along the near hill, away from the candle's corner
  ctx.fillStyle = '#0b0414';
  for (const [tx, ts] of [[470, 1], [545, 0.8], [640, 1.15], [735, 0.85], [830, 1]]) {
    const w = 26 * ts;
    const h = 40 * ts;
    ctx.beginPath();
    ctx.moveTo(tx - w / 2, H);
    ctx.lineTo(tx - w / 2, H - h + w / 2);
    ctx.arc(tx, H - h + w / 2, w / 2, Math.PI, 0);
    ctx.lineTo(tx + w / 2, H);
    ctx.closePath();
    ctx.fill();
  }

  drawPumpkin(ctx, 985, H - 34, 30);
  drawPumpkin(ctx, 1050, H - 24, 21);

  // vignette: the words live in the middle
  const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, W * 0.72);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.5)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);
}

// ── ghost ─────────────────────────────────────────────────────────────────────────────────────

const KEY_GHOST = 'hw_ghost';
const GHOST_W = 150;
const GHOST_H = 190;

function paintGhost(ctx: Ctx): void {
  const cx = GHOST_W / 2;
  ctx.shadowColor = 'rgba(190,210,255,0.9)';
  ctx.shadowBlur = 26;
  const body = ctx.createLinearGradient(0, 20, 0, GHOST_H - 20);
  body.addColorStop(0, '#ffffff');
  body.addColorStop(1, '#cfd9ff');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(cx - 46, GHOST_H - 34);
  ctx.lineTo(cx - 46, 70);
  ctx.bezierCurveTo(cx - 46, 14, cx + 46, 14, cx + 46, 70);
  ctx.lineTo(cx + 46, GHOST_H - 34);
  // wavy hem
  for (let i = 0; i < 4; i++) {
    const x1 = cx + 46 - i * 23;
    ctx.quadraticCurveTo(x1 - 6, GHOST_H - 14, x1 - 11.5, GHOST_H - 34 + (i % 2 ? -6 : 0));
    ctx.quadraticCurveTo(x1 - 17, GHOST_H - 52 + (i % 2 ? 0 : 0), x1 - 23, GHOST_H - 34);
  }
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  // stubby arms
  ctx.fillStyle = '#e4ebff';
  ctx.beginPath();
  ctx.ellipse(cx - 52, 104, 11, 20, 0.5, 0, Math.PI * 2);
  ctx.ellipse(cx + 52, 104, 11, 20, -0.5, 0, Math.PI * 2);
  ctx.fill();
  // face: hollow eyes and an "oooh" mouth
  ctx.fillStyle = '#1b1236';
  ctx.beginPath();
  ctx.ellipse(cx - 17, 74, 9, 14, 0, 0, Math.PI * 2);
  ctx.ellipse(cx + 17, 74, 9, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx, 112, 10, 15, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,170,190,0.55)';
  ctx.beginPath();
  ctx.ellipse(cx - 33, 94, 8, 5, 0, 0, Math.PI * 2);
  ctx.ellipse(cx + 33, 94, 8, 5, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * One ghost, drifting across the sky behind the board for as long as the scene lives: in from one
 * side, a lazy bob up and down, out the other, a pause, then back the other way at another height.
 * Part see-through, and behind the board, so it never hides a word.
 */
function flyGhost(scene: Phaser.Scene): void {
  const ghost = scene.add.image(-200, 240, KEY_GHOST).setAlpha(0.6).setScale(1).setDepth(5);
  let rightwards = Math.random() < 0.5;
  let bob: Phaser.Tweens.Tween | null = null;

  // It fades in and out a little as it drifts, like something half there.
  scene.tweens.add({
    targets: ghost, alpha: { from: 0.5, to: 0.75 }, duration: 2400, yoyo: true, repeat: -1,
    ease: 'Sine.easeInOut',
  });

  const cross = (): void => {
    const y0 = Phaser.Math.Between(70, 430);
    ghost.setFlipX(!rightwards);
    ghost.setPosition(rightwards ? -120 : GAME_WIDTH + 120, y0);
    // The lazy bob is its own endless tween on y, replaced for each crossing's new height.
    bob?.remove();
    bob = scene.tweens.add({
      targets: ghost, y: y0 + 34, duration: 1700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    scene.tweens.add({
      targets: ghost,
      x: rightwards ? GAME_WIDTH + 120 : -120,
      duration: Phaser.Math.Between(15000, 21000),
      ease: 'Sine.easeInOut',
      onComplete: () => {
        rightwards = !rightwards;
        scene.time.delayedCall(Phaser.Math.Between(2500, 7000), cross);
      },
    });
  };
  scene.time.delayedCall(Phaser.Math.Between(900, 2500), cross);
}

// ── candle ────────────────────────────────────────────────────────────────────────────────────

const KEY_CANDLE = 'hw_candle';
const KEY_FLAME = 'hw_flame';
const KEY_GLOW = 'hw_glow';
const CANDLE_W = 76;
const CANDLE_H = 150;
/** Where the wick's tip is inside the candle texture. */
const WICK_Y = 40;

function paintCandle(ctx: Ctx): void {
  const cx = CANDLE_W / 2;
  // saucer
  ctx.fillStyle = '#3b2c20';
  ctx.beginPath();
  ctx.ellipse(cx, CANDLE_H - 10, 36, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#5a4430';
  ctx.beginPath();
  ctx.ellipse(cx, CANDLE_H - 14, 33, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  // body, lit from the flame side (top) and shaded to the edges
  const body = ctx.createLinearGradient(cx - 18, 0, cx + 18, 0);
  body.addColorStop(0, '#c9b78c');
  body.addColorStop(0.45, '#f6ead0');
  body.addColorStop(1, '#bba97e');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(cx - 18, CANDLE_H - 16);
  ctx.lineTo(cx - 18, 56);
  ctx.ellipse(cx, 56, 18, 6, 0, Math.PI, 0);
  ctx.lineTo(cx + 18, CANDLE_H - 16);
  ctx.ellipse(cx, CANDLE_H - 16, 18, 6, 0, 0, Math.PI);
  ctx.closePath();
  ctx.fill();
  // melted rim and two drips running down
  ctx.fillStyle = '#fff6e0';
  ctx.beginPath();
  ctx.ellipse(cx, 56, 18, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#efe0bd';
  for (const [dx, len] of [[-14, 30], [6, 18]]) {
    ctx.beginPath();
    ctx.moveTo(cx + dx, 58);
    ctx.lineTo(cx + dx, 58 + len);
    ctx.arc(cx + dx + 3.5, 58 + len, 3.5, Math.PI, 0, true);
    ctx.lineTo(cx + dx + 7, 58);
    ctx.closePath();
    ctx.fill();
  }
  // wick
  ctx.strokeStyle = '#2a1c14';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx, 56);
  ctx.quadraticCurveTo(cx + 2, 48, cx, WICK_Y);
  ctx.stroke();
}

const FLAME_W = 44;
const FLAME_H = 78;

function paintFlame(ctx: Ctx): void {
  const cx = FLAME_W / 2;
  const g = ctx.createRadialGradient(cx, FLAME_H - 22, 2, cx, FLAME_H - 26, 32);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(0.25, '#ffe27a');
  g.addColorStop(0.65, '#ff9a1f');
  g.addColorStop(1, 'rgba(255,90,10,0.85)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(cx, 2);
  ctx.bezierCurveTo(cx + 6, 26, FLAME_W - 3, 40, FLAME_W - 5, FLAME_H - 26);
  ctx.bezierCurveTo(FLAME_W - 6, FLAME_H - 6, cx + 8, FLAME_H - 1, cx, FLAME_H - 1);
  ctx.bezierCurveTo(cx - 8, FLAME_H - 1, 6, FLAME_H - 6, 5, FLAME_H - 26);
  ctx.bezierCurveTo(3, 40, cx - 6, 26, cx, 2);
  ctx.fill();
}

function paintGlow(ctx: Ctx): void {
  const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  g.addColorStop(0, 'rgba(255,170,60,0.6)');
  g.addColorStop(0.4, 'rgba(255,130,30,0.22)');
  g.addColorStop(1, 'rgba(255,110,20,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
}

/** A candle burning in the lower-left corner, its flame guttering and its glow breathing. */
function burnCandle(scene: Phaser.Scene): void {
  const x = 104;                               // clear of the speaker glyph in the corner
  const baseY = GAME_HEIGHT - 4;
  const candleTop = baseY - CANDLE_H;
  const flameX = x;
  const flameY = candleTop + WICK_Y + 6;       // the flame's foot sits on the wick

  scene.add.image(x, baseY, KEY_CANDLE).setOrigin(0.5, 1).setDepth(8);
  const glow = scene.add.image(flameX, flameY - 22, KEY_GLOW)
    .setBlendMode(Phaser.BlendModes.ADD).setDepth(7).setScale(1.5).setAlpha(0.8);
  const flame = scene.add.image(flameX, flameY, KEY_FLAME).setOrigin(0.5, 1).setDepth(9).setScale(0.8);

  const flicker = (): void => {
    const d = Phaser.Math.Between(70, 170);
    scene.tweens.add({
      targets: flame,
      scaleX: Phaser.Math.FloatBetween(0.7, 0.92),
      scaleY: Phaser.Math.FloatBetween(0.72, 0.98),
      angle: Phaser.Math.FloatBetween(-5, 5),
      duration: d, ease: 'Sine.easeInOut',
      onComplete: flicker,
    });
    scene.tweens.add({
      targets: glow,
      alpha: Phaser.Math.FloatBetween(0.6, 0.95),
      scale: Phaser.Math.FloatBetween(1.35, 1.65),
      duration: d, ease: 'Sine.easeInOut',
    });
  };
  flicker();
}

// ── the backdrop scene's whole Halloween set ──────────────────────────────────────────────────

/**
 * Everything BackdropScene draws for the Halloween lesson, in place of the player's chosen
 * background: the night, the drifting ghost, the burning candle. Safe to call every round.
 */
export function buildHalloweenBackdrop(scene: Phaser.Scene): void {
  paintOnce(scene, KEY_BG, GAME_WIDTH, GAME_HEIGHT, paintBackdrop);
  paintOnce(scene, KEY_GHOST, GHOST_W, GHOST_H, paintGhost);
  paintOnce(scene, KEY_CANDLE, CANDLE_W, CANDLE_H, paintCandle);
  paintOnce(scene, KEY_FLAME, FLAME_W, FLAME_H, paintFlame);
  paintOnce(scene, KEY_GLOW, 256, 256, paintGlow);

  scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, KEY_BG).setDepth(0);
  flyGhost(scene);
  burnCandle(scene);
}

// ── spider ────────────────────────────────────────────────────────────────────────────────────

const KEY_SPIDER = 'hw_spider';
const SPIDER_SIZE = 128;

function paintSpider(ctx: Ctx): void {
  const cx = SPIDER_SIZE / 2;
  const thorax = { x: cx, y: 54 };
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // legs: four a side, each a knee out and a foot down, drawn as a dark outline under a light line
  const legs = [-1, 1].flatMap((side) => [
    [28, -10, 54, 6], [34, 4, 58, 24], [34, 18, 54, 44], [26, 30, 42, 58],
  ].map(([kx, ky, fx, fy]) => ({ side, kx, ky, fx, fy })));
  for (const [width, color] of [[9, '#0d0716'], [5, '#8a72c8']] as const) {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    for (const l of legs) {
      ctx.beginPath();
      ctx.moveTo(thorax.x, thorax.y);
      ctx.lineTo(thorax.x + l.side * l.kx, thorax.y + l.ky - 14);
      ctx.lineTo(thorax.x + l.side * l.fx, thorax.y + l.fy - 6);
      ctx.stroke();
    }
  }
  // abdomen with an orange hourglass, then the head
  ctx.fillStyle = '#1b1230';
  ctx.strokeStyle = '#ff9a1f';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(cx, 78, 22, 28, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ff9a1f';
  ctx.beginPath();
  ctx.moveTo(cx - 8, 66);
  ctx.lineTo(cx + 8, 66);
  ctx.lineTo(cx, 78);
  ctx.lineTo(cx + 8, 90);
  ctx.lineTo(cx - 8, 90);
  ctx.lineTo(cx, 78);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#1b1230';
  ctx.beginPath();
  ctx.ellipse(cx, 40, 17, 15, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // googly eyes looking where it is going (up)
  for (const sx of [-1, 1]) {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx + sx * 7, 36, 6.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#120b1f';
    ctx.beginPath();
    ctx.arc(cx + sx * 7, 34, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * The spider that takes the ladybird's place on the Halloween lesson: drawn once, head up (the same
 * orientation BoardScene.crawlToNextWord turns by), with a quick fidget so it reads as walking.
 * About the ladybird's size, a little bigger for the legs.
 */
export function buildSpider(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Container {
  paintOnce(scene, KEY_SPIDER, SPIDER_SIZE, SPIDER_SIZE, paintSpider);
  const c = scene.add.container(x, y).setDepth(60);
  const img = scene.add.image(0, 8, KEY_SPIDER).setScale(0.9);
  c.add(img);
  scene.tweens.add({
    targets: img, angle: { from: -5, to: 5 }, scaleX: { from: 0.88, to: 0.94 },
    duration: 110, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
  });
  return c;
}
